import type { FenceConfig, HouseBodyConfig, LotConfig, MailboxConfig } from '@/model/schema';
import type { ResolvedConstraints } from './constraints';
import type { StylePresetSpec } from './stylePresets';
import type { Rng } from './rng';

export interface Accessories {
  fence?: FenceConfig;
  mailbox?: MailboxConfig;
}

/**
 * Fence rules from design doc:
 *   - offset from lot edge (the "inset"), or aligned to a grammar
 *   - gate placement corresponds to front path / entry side
 *   - small lots: avoid (would look cluttered)
 *
 * Mailbox rules:
 *   - near entry path / gate
 *   - no intersection with fence posts or door clearance
 */
export function generateAccessories(
  rng: Rng,
  lot: LotConfig,
  body: HouseBodyConfig,
  preset: StylePresetSpec,
  constraints: ResolvedConstraints,
): Accessories {
  const lotArea = lot.width * lot.depth;
  const smallLot = lotArea < 120; // ~ less than 12m x 10m

  const shouldFence = constraints.fence.hard
    ? constraints.fence.value
    : (!smallLot && rng.chance(preset.fenceChance));

  const shouldMailbox = constraints.mailbox.hard
    ? constraints.mailbox.value
    : rng.chance(preset.mailboxChance);

  const result: Accessories = {};

  if (shouldFence) {
    const inset = rng.range(0.25, 0.6);
    const postSpacing = rng.range(1.3, 1.9);
    const gateOffset = lot.width / 2 + rng.range(-1.0, 1.0);
    result.fence = {
      height: rng.range(1.1, 1.5),
      inset,
      postSpacing,
      gateSide: 'front',
      gateOffset: Math.max(0.5, Math.min(lot.width - 1.5, gateOffset)),
      // Default — `generateHouse` overrides with a per-house RNG style pick.
      style: 'rail',
    };
  }

  if (shouldMailbox) {
    // Attach to fence when possible, on the OPPOSITE side of the gate from
    // the entry walkway. The walkway leg that joins door → gate is ~1.1 m
    // wide and pads another 0.55 m on each side, so we keep at least
    // 1.4 m between gate centre and mailbox post (mailbox half-width is
    // ~0.16 m, plus a small breathing gap).
    const attachedToFence = !!result.fence && rng.chance(0.7);
    const MAILBOX_GATE_GAP = 1.4;
    const x = attachedToFence
      ? (-lot.width / 2 + (result.fence!.gateOffset - MAILBOX_GATE_GAP))
      : -lot.width / 2 + rng.range(1.5, Math.max(1.6, lot.width / 2));
    const z = -lot.depth / 2 + (attachedToFence ? (result.fence!.inset + 0.2) : 0.6);
    result.mailbox = {
      x,
      z,
      height: rng.range(1.0, 1.2),
      attachedToFence,
    };
  }

  // Suppress accessories that would intersect the house body outline.
  if (result.fence) {
    const bodyFrontZ = body.originZ; // lower-Z edge of body
    const fenceFrontZ = -lot.depth / 2 + result.fence.inset;
    if (fenceFrontZ > bodyFrontZ - 0.5) {
      // Fence would be inside/behind the body, drop it.
      delete result.fence;
    }
  }

  return result;
}
